import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { createCampaign } from '../Services/services/dashboadapi';

// Async thunk to fetch a campaign by ID
export const fetchCampaignById = createAsyncThunk(
  'campaign/fetchCampaignById',
  async (campaignId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL_DASHBOARD_URL}/campaign/${campaignId}`);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk to update a campaign
export const updateCampaign = createAsyncThunk(
  'campaign/updateCampaign',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${import.meta.env.VITE_API_URL_DASHBOARD_URL}/campaign/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


// Existing async thunks for uploading documents and creating a campaign
export const uploadDocuments = createAsyncThunk(
  'campaign/uploadDocuments',
  async (document1, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append("file", document1);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/images/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.status === 200) {
        return response.data.message;
      } else {
        return rejectWithValue(response.data.message || "Unknown error");
      }
    } catch (error) {
      return rejectWithValue(error.message || "An error occurred while uploading the documents.");
    }
  }
);

export const createCampaignAsync = createAsyncThunk(
  'campaign/createCampaign',
  async (campaignData, { rejectWithValue }) => {
    try {
      const response = await createCampaign(campaignData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create campaign.");
    }
  }
);

export const fetchCampaigns = createAsyncThunk(
  'campaign/fetchCampaigns',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL_DASHBOARD_URL}/campaign`, { params });
      console.log('Fetched Campaigns:', response.data); // Add this line
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.error[0]);
    }
  }
);


const campaignSlice = createSlice({
  name: 'campaign',
  initialState: {
    campaigns: [],
    currentCampaign: null,
    impacts: [],
    documentUploadStatus: null,
    campaignStatus: null,
    error: null,
  },
  reducers: {
    addImpact: (state, action) => {
      state.impacts.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch campaign by ID
      .addCase(fetchCampaignById.pending, (state) => {
        state.campaignStatus = 'loading';
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.campaignStatus = 'succeeded';
        state.currentCampaign = action.payload;
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.campaignStatus = 'failed';
        state.error = action.payload;
      })
      // Update campaign
      .addCase(updateCampaign.pending, (state) => {
        state.campaignStatus = 'loading';
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.campaignStatus = 'succeeded';
        const index = state.campaigns.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.campaignStatus = 'failed';
        state.error = action.payload;
      })
      // Upload documents
      .addCase(uploadDocuments.pending, (state) => {
        state.documentUploadStatus = 'loading';
      })
      .addCase(uploadDocuments.fulfilled, (state, action) => {
        state.documentUploadStatus = 'succeeded';
        state.file = action.payload;
      })
      .addCase(uploadDocuments.rejected, (state, action) => {
        state.documentUploadStatus = 'failed';
        state.error = action.payload;
      })
      // Create campaign
      .addCase(createCampaignAsync.pending, (state) => {
        state.campaignStatus = 'loading';
      })
      .addCase(createCampaignAsync.fulfilled, (state, action) => {
        state.campaignStatus = 'succeeded';
        state.campaign = action.payload;
      })
      .addCase(createCampaignAsync.rejected, (state, action) => {
        state.campaignStatus = 'failed';
        state.error = action.payload;
      })
      // Fetch campaigns
      .addCase(fetchCampaigns.pending, (state) => {
        state.campaignStatus = 'loading';
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.campaignStatus = 'succeeded';
        state.campaigns = action.payload.docs;
        state.totalDocs = action.payload.totalDocs;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.campaignStatus = 'failed';
        state.error = action.payload;
      });
  },
});

export const { addImpact } = campaignSlice.actions;
export default campaignSlice.reducer;
